<template>
  <NoResult v-if="!hasFavorites && hasFetchedOnce" />

  <RevContainer v-if="hasFavorites || !hasFetchedOnce">
    <div class="mb-32 space-y-4">
      <RevSkeleton
        v-if="!hasFetchedOnce"
        height="24px"
        shape="line"
        width="250px"
      />
      <h2 v-else class="heading-1">{{ i18n(translations.pageTitle) }}</h2>

      <RevSkeleton
        v-if="!hasFetchedOnce"
        height="24px"
        shape="line"
        width="150px"
      />
      <p v-else class="body-1">
        {{ i18n(translations.itemsCount, { count: favoritesCount }) }}
      </p>

      <AppDownloadBanner
        v-if="hasMobileApp"
        :description="i18n(translations.bannerDescription)"
        :is-loading="!hasFetchedOnce"
        :title="i18n(translations.bannerTitle)"
        trackingZone="favorites"
      />
    </div>

    <CardsList
      :favorites="favoritesToDisplay"
      :is-loading="!hasFetchedOnce"
      @count-updated="updateFavoritesCount"
      @favorite-removed="removeFavorite"
    />
  </RevContainer>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevContainer } from '@ds/components/Container'
import { RevSkeleton } from '@ds/components/Skeleton'

import { features } from '~/features/features'

import AppDownloadBanner from '../../lead-collection/components/AppDownload/AppDownload.vue'

import translations from './Favorites.translations'
import CardsList from './cards/CardsList.vue'
import { useFetchFavorites } from './composables/useFetchFavorites'
import NoResult from './no-result/NoResult.vue'
import { fromAPIFavorite as formatToFavoriteTrackingModel } from './utils/FavoriteTrackingModel'
import {
  type FavoriteViewModel,
  fromAPIFavorite as formatToFavoriteViewModel,
} from './utils/FavoriteViewModel'

const i18n = useI18n()
const tracking = useTracking()
const {
  market: { countryCode },
} = useMarketplace()

const favoritesToDisplay = ref<FavoriteViewModel[]>([])

const { data: fetchedFavorites, hasFetchedOnce } = useFetchFavorites({
  onDataUpdated: (favorites) => {
    trackPageView()
    updateFavoritesCount(favorites?.length ?? 0)

    if (!favorites || isEmpty(favorites) || (favorites?.length ?? 0) === 0) {
      favoritesToDisplay.value = []

      return
    }

    favoritesToDisplay.value = favorites.map((favorite) => {
      return formatToFavoriteViewModel(favorite)
    })
  },
})

const manualFavoritesCount = ref<number | null>(null)

const favoritesCount = computed(
  () => manualFavoritesCount.value ?? fetchedFavorites.value?.length ?? 0,
)

const hasFavorites = computed(() => {
  return !isEmpty(favoritesToDisplay.value)
})

const trackingInfo = computed(() => {
  const apiFavorites = !isEmpty(fetchedFavorites.value)
    ? fetchedFavorites.value
    : []
  const favoriteItems = apiFavorites ?? []

  const trackingFavorites = favoriteItems.map((favorite) => {
    return formatToFavoriteTrackingModel(favorite)
  })

  return {
    pageType: 'my_favorites',
    favorites: trackingFavorites,
  }
})

const hasMobileApp = computed(() => {
  return features[countryCode].mobileApp.available
})

function trackPageView() {
  tracking.trackFavoritesPage(trackingInfo.value)
}

function updateFavoritesCount(updatedCount: number) {
  manualFavoritesCount.value = updatedCount
}

function removeFavorite(favoriteUuid: string) {
  favoritesToDisplay.value = favoritesToDisplay.value.filter(
    (favorite) => favorite.favoriteUuid !== favoriteUuid,
  )
}
</script>
